<template>
  <!-- Um elemento raiz só: o eslint do projeto ainda roda a regra `vue/no-multiple-template-root`. -->
  <div class="loja-de-varas">
    <LojaPainel
      :itens="varas"
      :carregando="carregando"
      :erro="erro || ''"
      :saldo="ouro"
      :nivel-do-jogador="nivel"
      :efeito-de-preco="efeitoDePreco"
      icone-vazio="nav-pescar"
      @selecionar="comentarSelecao"
      @comprar="pedirConfirmacao"
      @bloqueado="comentarBloqueio"
      @recarregar="carregar"
      />

    <!--
    O D11 manda: **ação reversível não pede confirmação.** Comprar uma vara gasta ouro e não é
    reversível, então a confirmação fica — é exatamente o caso que a regra preserva.
  -->
    <PurchaseConfirmModal
      v-if="mostrarConfirmacao"
      :show="mostrarConfirmacao"
      :item="itemEscolhido"
      :quantity="1"
      :total-price="precoTotal"
      @confirm="confirmar"
      @cancel="mostrarConfirmacao = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LojaPainel from './LojaPainel.vue';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal.vue';
import { falaDoLojista } from './falas';

/**
 * A loja de varas — o Velho João (D16).
 *
 * ## O que sumiu daqui, e é o ponto
 *
 * Sumiram **o cabeçalho e a pílula de ouro**. Cada uma das seis lojas desenhava a sua (UI-16), em
 * posição e estilo diferentes, enquanto o `Header` já tinha um HUD bom. Agora o ouro mora no
 * `FishHud` do cabeçalho e nada mais o redesenha.
 *
 * Sumiu também a **lista de `ItemCard`** — o paredão. O `LojaPainel` faz grade + detalhe, e é o
 * mesmo painel nas seis lojas, então elas param de divergir.
 *
 * ## O que sobrou
 *
 * Buscar as varas, saber o preço e comprar. É o que uma loja específica deve saber; o resto é
 * apresentação, e apresentação virou primitivo.
 *
 * ## O `recado`
 *
 * O NPC comenta a seleção, a compra e o ouro que falta (5.2). A sub-loja emite; o `Shop.vue` põe no
 * balão. É por isso que o `NpcStage` recebe a fala por prop em vez de a sub-loja falar direto: o
 * balão pertence ao palco, não à prateleira.
 *
 * **Qual fala** é decisão do `falas.js`: cada lojista tem as dele no i18n, sorteadas e nunca
 * repetidas duas vezes seguidas. Aqui a loja só diz *o momento* — `about`, `bought`, `cannot`.
 */
export default {
  name: 'RodShop',
  components: { LojaPainel, PurchaseConfirmModal },
  emits: ['interact', 'recado'],
  data() {
    return {
      /** A chave desta loja no i18n — de onde saem as falas do Velho João (`falas.js`). */
      tipo: 'rod',
      mostrarConfirmacao: false,
      itemEscolhido: null,
      precoTotal: 0,
      efeitoDePreco: 0,
    };
  },
  computed: {
    ...mapState({
      varas: (state) => state.shop.rods || [],
      carregando: (state) => state.shop.isLoading,
      erro: (state) => state.shop.error,
      ouro: (state) => state.player.gold,
      nivel: (state) => state.player.level || 1,
    }),
  },
  async mounted() {
    await this.carregar();
  },
  methods: {
    async carregar() {
      await Promise.all([
        this.$store.dispatch('shop/fetchRods'),
        this.$store
          .dispatch('shop/calculateRodPriceEffect')
          .then((v) => { this.efeitoDePreco = v || 0; })
          .catch(() => { this.efeitoDePreco = 0; }),
      ]);
    },
    /** Cada fala é **sorteada** entre as do lojista, e nunca a mesma duas vezes seguidas. */
    dizer(chave, params) {
      this.$emit('recado', falaDoLojista(this, this.tipo, chave, params));
    },
    comentarSelecao(item) {
      this.dizer('about', { name: item.name });
    },
    comentarBloqueio(motivo) {
      if (motivo) this.dizer('cannot', { reason: motivo });
    },
    pedirConfirmacao({ item, total }) {
      this.itemEscolhido = item;
      this.precoTotal = total;
      this.mostrarConfirmacao = true;
    },
    async confirmar() {
      try {
        const ok = await this.$store.dispatch('shop/purchaseRod', {
          rodId: this.itemEscolhido.id,
          price: this.precoTotal,
        });
        if (ok) {
          this.dizer('bought', { name: this.itemEscolhido.name });
          this.mostrarConfirmacao = false;
        }
      } catch {
        // O erro vira fala do NPC em vez de um alerta genérico: é o mesmo dado, dito por alguém.
        this.dizer('failed');
      }
    },
  },
};
</script>
